import React from 'react';
import { useTranslation } from 'react-i18next';
import {
  Link2,
  ArrowRight,
  ArrowLeft,
  Settings
} from 'lucide-react';
import { SOCIAL_PLATFORMS, ADS_PLATFORMS, PlatformDefinition } from '../../constants';

interface ConnectAccountsStepProps {
  onNext: () => void;
  onBack: () => void;
}

const ConnectAccountsStep: React.FC<ConnectAccountsStepProps> = ({
  onNext,
  onBack
}) => {
  const { t } = useTranslation();

  const renderPlatformCard = (platform: PlatformDefinition) => {
    return (
      <div
        key={platform.id}
        className="flex items-center space-x-3 p-3 rounded-xl border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800/50"
      >
        <div className={`w-10 h-10 rounded-lg bg-gradient-to-br ${platform.color} flex items-center justify-center text-white flex-shrink-0`}>
          {platform.icon}
        </div>
        <div>
          <div className="font-medium text-gray-900 dark:text-white text-sm">
            {platform.name}
          </div>
          <div className="text-xs text-gray-500 dark:text-gray-400">
            {t(platform.descriptionKey)}
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="p-5 sm:p-12">
      {/* Header */}
      <div className="text-center mb-8">
        <div className="inline-flex items-center justify-center w-16 h-16 rounded-2xl bg-gradient-to-br from-indigo-500 to-purple-500 mb-4 shadow-lg">
          <Link2 className="h-8 w-8 text-white" />
        </div>
        <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">
          {t('onboarding.connect.title', 'Connect your accounts')}
        </h2>
        <p className="text-gray-600 dark:text-gray-400">
          {t('onboarding.connect.subtitle', 'Connect your social media to publish and analyze automatically')}
        </p>
      </div>

      <div className="max-w-2xl mx-auto space-y-8">
        {/* Info box */}
        <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-xl p-4">
          <div className="flex items-start space-x-3">
            <Settings className="h-5 w-5 text-blue-600 dark:text-blue-400 mt-0.5 flex-shrink-0" />
            <div>
              <p className="text-sm text-blue-800 dark:text-blue-200 font-medium">
                {t('onboarding.connect.configureInSettings', 'Configure in Settings')}
              </p>
              <p className="text-sm text-blue-600 dark:text-blue-300 mt-1">
                {t('onboarding.connect.configureInSettingsDesc', 'After completing the onboarding, you can connect your accounts in the Settings page. This step is optional.')}
              </p>
            </div>
          </div>
        </div>

        {/* Available platforms preview */}
        <div>
          <h3 className="text-sm font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-4">
            {t('onboarding.connect.availablePlatforms', 'Available platforms')}
          </h3>

          {/* Social Media */}
          <div className="mb-6">
            <p className="text-xs text-gray-500 dark:text-gray-400 mb-2">
              {t('onboarding.connect.socialMedia', 'Social Media')}
            </p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
              {SOCIAL_PLATFORMS.map(renderPlatformCard)}
            </div>
          </div>

          {/* Ads Platforms */}
          <div>
            <p className="text-xs text-gray-500 dark:text-gray-400 mb-2">
              {t('onboarding.connect.adsPlatforms', 'Advertising Platforms')}
            </p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
              {ADS_PLATFORMS.map(renderPlatformCard)}
            </div>
          </div>
        </div>
      </div>

      {/* Navigation */}
      <div className="flex justify-between items-center mt-10 pt-6 border-t border-gray-200 dark:border-gray-700">
        <button
          onClick={onBack}
          className="flex items-center px-4 py-2 text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white transition-colors"
        >
          <ArrowLeft className="h-4 w-4 mr-2" />
          {t('common.back', 'Back')}
        </button>

        <button
          onClick={onNext}
          className="flex items-center px-6 py-3 bg-gradient-to-r from-primary-600 to-purple-600 text-white rounded-xl font-semibold shadow-lg shadow-primary-500/30 hover:shadow-xl transition-all"
        >
          {t('common.next', 'Continue')}
          <ArrowRight className="h-4 w-4 ml-2" />
        </button>
      </div>
    </div>
  );
};

export default ConnectAccountsStep;
